import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { 
  Plus, 
  Search, 
  Filter, 
  Package,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Eye,
  Edit,
  ShoppingCart,
  Calendar,
  DollarSign
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { formatCurrency, formatDate } from '@/utils'

export const InventoryListPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [selectedMaterial, setSelectedMaterial] = useState('all')

  // These would come from API calls
  const inventoryBatches: any[] = [] // Empty - no mock data
  const materials = [
    { value: 'all', label: 'All Materials' },
    { value: 'sterling-silver', label: 'Sterling Silver Wire' },
    { value: 'gold-filled', label: 'Gold Filled Wire' },
    { value: 'gemstones', label: 'Gemstones' }
  ]

  const filters = [
    { value: 'all', label: 'All Batches' },
    { value: 'active', label: 'Active Batches' },
    { value: 'depleted', label: 'Depleted Batches' },
    { value: 'low-stock', label: 'Low Stock' }
  ]

  const stats = [
    {
      title: 'Total Inventory Value',
      value: '$0.00',
      change: '+0%',
      trend: 'up' as 'up' | 'down' | 'neutral',
      icon: DollarSign
    },
    {
      title: 'Active Batches',
      value: '0',
      change: '+0',
      trend: 'up' as 'up' | 'down' | 'neutral',
      icon: Package
    },
    {
      title: 'Low Stock Items',
      value: '0',
      change: '0',
      trend: 'neutral' as 'up' | 'down' | 'neutral',
      icon: AlertTriangle
    },
    {
      title: 'Recent Purchases',
      value: '0',
      change: '+0',
      trend: 'up' as 'up' | 'down' | 'neutral',
      icon: ShoppingCart
    }
  ]

  return (
    <>
      <Helmet>
        <title>Inventory - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Inventory Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Track inventory batches, purchases, and stock movements with FIFO cost calculation
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/inventory/purchase">
              <Button variant="outline" leftIcon={<ShoppingCart className="w-4 h-4" />}>
                Record Purchase
              </Button>
            </Link>
            <Link to="/inventory/adjustment">
              <Button leftIcon={<Plus className="w-4 h-4" />}>
                Stock Adjustment
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div key={stat.title} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {stat.value}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${
                  stat.trend === 'up' 
                    ? 'bg-green-100 dark:bg-green-900/20' 
                    : stat.trend === 'down'
                    ? 'bg-red-100 dark:bg-red-900/20'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <stat.icon className={`w-6 h-6 ${
                    stat.trend === 'up' 
                      ? 'text-green-600 dark:text-green-400' 
                      : stat.trend === 'down'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`} />
                </div>
              </div>
              <div className="flex items-center mt-4">
                {stat.trend === 'up' ? (
                  <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400 mr-1" />
                ) : stat.trend === 'down' ? (
                  <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400 mr-1" />
                ) : null}
                <span className={`text-sm ${
                  stat.trend === 'up' 
                    ? 'text-green-600 dark:text-green-400' 
                    : stat.trend === 'down'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">
                  from last month
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Filters and Search */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by material, batch ID, or supplier..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Material Filter */}
            <div className="w-full lg:w-48">
              <select
                value={selectedMaterial}
                onChange={(e) => setSelectedMaterial(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              >
                {materials.map((material) => (
                  <option key={material.value} value={material.value}>
                    {material.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="w-full lg:w-48">
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              >
                {filters.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Inventory Batches */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {inventoryBatches.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No inventory batches found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Start by recording your first material purchase to track inventory with FIFO costing.
              </p>
              <Link to="/inventory/purchase">
                <Button leftIcon={<ShoppingCart className="w-4 h-4" />}>
                  Record First Purchase
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Batch ID
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Material
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Purchase Date
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Quantity
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Unit Cost
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Remaining
                    </th>
                    <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Status
                    </th>
                    <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryBatches.map((batch) => (
                    <tr
                      key={batch.id}
                      className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="py-4 px-6">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {batch.batchId}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {batch.supplier}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {batch.material.name}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {batch.material.sku}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-900 dark:text-white">
                        {formatDate(batch.purchaseDate)}
                      </td>
                      <td className="py-4 px-6 text-gray-900 dark:text-white">
                        {batch.originalQuantity} {batch.material.unitType}
                      </td>
                      <td className="py-4 px-6 text-gray-900 dark:text-white">
                        {formatCurrency(batch.unitCost)}
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-gray-900 dark:text-white">
                          {batch.remainingQuantity} {batch.material.unitType}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {Math.round((batch.remainingQuantity / batch.originalQuantity) * 100)}% remaining
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <Badge 
                          variant={
                            batch.remainingQuantity === 0 
                              ? 'secondary' 
                              : batch.remainingQuantity < batch.originalQuantity * 0.2
                              ? 'warning'
                              : 'success'
                          }
                        >
                          {batch.remainingQuantity === 0 
                            ? 'Depleted' 
                            : batch.remainingQuantity < batch.originalQuantity * 0.2
                            ? 'Low Stock'
                            : 'Active'
                          }
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-end gap-2">
                          <Link to={`/inventory/batch/${batch.id}`}>
                            <Button variant="outline" size="sm">
                              <Eye className="w-3 h-3" />
                            </Button>
                          </Link>
                          <Link to={`/inventory/adjust/${batch.id}`}>
                            <Button variant="outline" size="sm">
                              <Edit className="w-3 h-3" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}